/**
 * Dashboard rebuild — token re-exports and ECharts theme helper (Agent A4).
 *
 * Ergonomic entry point for code under `webapp/frontend/src/dashboard/`.
 * The canonical token table lives in `src/theme.ts` (`dashboardTokens`); this
 * module re-exports it and exposes `dashboardChartTheme()` which merges the
 * PBI palette, dotted gridlines, and Inter typography into an ECharts option.
 *
 * Agents A7 (metrics table + view selectors) and A8 (combo charts) call
 * `dashboardChartTheme()` immediately before passing options to ECharts so
 * every visual in the rebuild shares one chart look.
 */

import { dashboardTokens } from "../theme";

export { dashboardTokens };
export type DashboardTokens = typeof dashboardTokens;

/* ------------------------------------------------------------------------- *
 * ECharts theme helper
 * ------------------------------------------------------------------------- */

/**
 * Read a `--dash-*` CSS var off the document root at CALL TIME so the chart
 * theme reflects the active light/dark mode. Falls back to the static
 * `dashboardTokens` value (light theme) in SSR/test contexts where the
 * stylesheet isn't applied. `dashboardChartTheme()` runs inside each chart's
 * `theme`-dependent `useMemo`, so these reads re-resolve when the user toggles.
 */
function readVar(name: string, fallback: string): string {
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (v) return v;
  }
  return fallback;
}

/**
 * Merge dashboard chart defaults (PBI palette, dotted gridlines, Inter font)
 * into an ECharts `option`. Caller-supplied values win; arrays are not deep-
 * merged element-wise, they replace. This mirrors how echarts-for-react's
 * `option` prop is overridden in practice.
 *
 * Usage:
 *   const option = dashboardChartTheme({
 *     xAxis: { type: "category", data: [...] },
 *     series: [{ type: "bar", data: [...] }],
 *   });
 *
 * Agents A7 and A8 should call this immediately before passing `option`
 * to `<ReactECharts option={option} />`.
 */
export function dashboardChartTheme(option: any): any {
  const incoming = (option ?? {}) as Record<string, any>;

  // Resolve theme-aware colors at call time (light or dark).
  const cFg        = readVar("--dash-fg", dashboardTokens.color.fg);
  const cHairline  = readVar("--dash-hairline", dashboardTokens.color.hairline);
  const cCardBg    = readVar("--dash-bg-card", dashboardTokens.color.cardBg);
  const cFgSubtle  = readVar("--dash-fg-subtle", dashboardTokens.color.fgSubtle);
  // Axis tick labels + legend use the dedicated, higher-contrast chart label
  // token so the numbers stay legible on the dark canvas (the bug where the
  // y-axis values disappeared in dark mode).
  const cAxisLabel = readVar("--dash-chart-axis-label", dashboardTokens.color.fgMuted);
  const cGridline  = readVar("--dash-chart-gridline", dashboardTokens.color.hairline);
  const series = Array.from({ length: 8 }, (_, i) =>
    readVar(`--dash-series-${i + 1}`, dashboardTokens.color.chartSeries[i] ?? cFg),
  );

  const axisLabelStyle = {
    color: cAxisLabel,
    fontFamily: dashboardTokens.font.family,
    fontSize: 12,
  };
  const axisTick = { show: false };

  const base = {
    color: series,
    backgroundColor: "transparent",
    textStyle: { fontFamily: dashboardTokens.font.family, fontSize: 12, color: cFg },
    grid: {
      left: 16,
      right: 16,
      top: 24,
      bottom: 24,
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: cCardBg,
      borderColor: cHairline,
      borderWidth: 1,
      padding: 10,
      textStyle: { color: cFg, fontFamily: dashboardTokens.font.family, fontSize: 12 },
      extraCssText: `box-shadow: ${dashboardTokens.shadow.popover}; border-radius: ${dashboardTokens.radius.control};`,
      axisPointer: {
        type: "line",
        lineStyle: { color: cFgSubtle, type: "dashed", width: 1 },
      },
    },
    legend: {
      textStyle: { color: cAxisLabel, fontFamily: dashboardTokens.font.family, fontSize: 12 },
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 16,
    },
    xAxis: {
      type: "category",
      axisLine: { show: true, lineStyle: { color: cHairline, width: 1 } },
      axisTick,
      axisLabel: axisLabelStyle,
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick,
      axisLabel: axisLabelStyle,
      splitLine: {
        show: true,
        lineStyle: { color: cGridline, type: "dotted" as const, width: 1 },
      },
    },
  };

  // Shallow merge with deep-merge for the well-known nested objects
  // (grid, tooltip, legend, xAxis, yAxis, textStyle). Series and other
  // arrays/values from `incoming` win outright.
  const merged: Record<string, any> = { ...base, ...incoming };

  if (incoming.textStyle) {
    merged.textStyle = { ...base.textStyle, ...incoming.textStyle };
  }
  if (incoming.grid && !Array.isArray(incoming.grid)) {
    merged.grid = { ...base.grid, ...incoming.grid };
  }
  if (incoming.tooltip && !Array.isArray(incoming.tooltip)) {
    merged.tooltip = { ...base.tooltip, ...incoming.tooltip };
  }
  if (incoming.legend && !Array.isArray(incoming.legend)) {
    merged.legend = { ...base.legend, ...incoming.legend };
  }
  if (incoming.xAxis && !Array.isArray(incoming.xAxis)) {
    merged.xAxis = {
      ...base.xAxis,
      ...incoming.xAxis,
      axisLine:  { ...base.xAxis.axisLine,  ...(incoming.xAxis.axisLine  ?? {}) },
      axisTick:  { ...base.xAxis.axisTick,  ...(incoming.xAxis.axisTick  ?? {}) },
      axisLabel: { ...base.xAxis.axisLabel, ...(incoming.xAxis.axisLabel ?? {}) },
      splitLine: { ...base.xAxis.splitLine, ...(incoming.xAxis.splitLine ?? {}) },
    };
  }
  if (incoming.yAxis && !Array.isArray(incoming.yAxis)) {
    merged.yAxis = {
      ...base.yAxis,
      ...incoming.yAxis,
      axisLine:  { ...base.yAxis.axisLine,  ...(incoming.yAxis.axisLine  ?? {}) },
      axisTick:  { ...base.yAxis.axisTick,  ...(incoming.yAxis.axisTick  ?? {}) },
      axisLabel: { ...base.yAxis.axisLabel, ...(incoming.yAxis.axisLabel ?? {}) },
      splitLine: { ...base.yAxis.splitLine, ...(incoming.yAxis.splitLine ?? {}) },
    };
  }

  // If `color` wasn't supplied by caller, base palette already wins;
  // if it was, the spread above used the caller's color. Both correct.
  return merged;
}
